<template>
  <section class="day-end-page">
    <header class="day-end-heading mb-5">
      <h1 class="text-h5 text-sm-h4 font-weight-bold">Day End</h1>
      <div class="d-flex align-center">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }"><v-btn icon :color="actionColor" min-width="44" min-height="44" :loading="loading" :disabled="loading" aria-label="Refresh Day End reports" v-bind="attrs" v-on="on" @click="loadDayEnds"><v-icon>mdi-refresh</v-icon></v-btn></template>
          <span>Refresh Day End reports</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }"><v-btn icon :color="actionColor" min-width="44" min-height="44" :aria-label="hasActiveFilters ? 'Filter Day End reports (filters applied)' : 'Filter Day End reports'" aria-haspopup="dialog" v-bind="attrs" v-on="on" @click="openFilters($event)"><v-icon>{{ hasActiveFilters ? 'mdi-filter-check' : 'mdi-filter-variant' }}</v-icon></v-btn></template>
          <span>{{ hasActiveFilters ? 'Filters applied' : 'Filter Day End reports' }}</span>
        </v-tooltip>
      </div>
      <p class="day-end-description text-body-2 admin-muted mb-0">Review closing reports, performance lights, and the shifts behind each day.</p>
    </header>

    <v-alert v-if="errorMessage || validationError" type="error" text class="rounded-lg" role="alert">
      <span class="text--primary">{{ validationError || errorMessage }}</span>
      <v-btn v-if="errorMessage && !validationError" text :color="$vuetify.theme.dark ? actionColor : 'primary darken-1'" min-height="44" @click="loadDayEnds">Try again</v-btn>
    </v-alert>
    <v-card v-else outlined class="admin-panel rounded-xl">
      <v-card-title class="d-block px-4 px-sm-5">
        <h2 class="text-subtitle-1 font-weight-bold">{{ loading ? 'Loading day ends…' : `${count.toLocaleString()} day ${count === 1 ? 'end' : 'ends'}` }}</h2>
        <p class="text-caption admin-muted font-weight-regular mb-0">{{ filterSummary }}</p>
      </v-card-title>
      <div class="day-end-table" tabindex="0" role="region" aria-label="Day End reports table">
      <v-data-table
        :headers="headers" :items="dayEnds" :loading="loading" :mobile-breakpoint="0"
        disable-sort disable-pagination hide-default-footer item-key="id" class="elevation-0 admin-data-table day-end-data-table"
        loading-text="Loading day ends…" no-data-text="No day ends match this location and date range."
        @click:row="(item, row, event) => openReport(item, event)"
      >
        <template v-slot:[`item.businessDate`]="{ item }"><div class="font-weight-medium day-end-date">{{ businessDate(item.businessDate) }}</div><div class="text-caption admin-muted">Day End #{{ item.localId || item.id }}</div></template>
        <template v-slot:[`item.location`]="{ item }"><span class="day-end-location">{{ item.Location && item.Location.name || 'Location' }}</span></template>
        <template v-slot:[`item.status`]="{ item }"><span>{{ statusLabel(item.status) }}</span></template>
        <template v-slot:[`item.lightStatus`]="{ item }"><span class="day-end-light"><v-icon small :color="lightColor(item.lightStatus)" aria-hidden="true">{{ lightIcon(item.lightStatus) }}</v-icon>{{ lightLabel(item.lightStatus) }}</span></template>
        <template v-slot:[`item.salesTotal`]="{ item }"><span class="day-end-money">{{ item.status === 'closed' ? money(item.salesTotal, item) : 'In progress' }}</span></template>
        <template v-slot:[`item.overShortTotal`]="{ item }"><span class="day-end-money">{{ item.status === 'closed' ? money(item.overShortTotal, item) : 'Not closed' }}</span></template>
        <template v-slot:[`item.action`]="{ item }"><v-btn icon :color="actionColor" min-width="44" min-height="44" :aria-label="`View Day End ${item.localId || item.id} for ${item.Location && item.Location.name || 'this location'}`" @click.stop="openReport(item, $event)"><v-icon>mdi-chevron-right</v-icon></v-btn></template>
      </v-data-table>
      </div>
      <v-divider v-if="count > pageSize" />
      <v-card-actions v-if="count > pageSize" class="day-end-pagination pa-4">
        <span class="text-caption admin-muted">{{ (page - 1) * pageSize + 1 }}–{{ Math.min(page * pageSize, count) }} of {{ count }}</span>
        <v-pagination v-model="page" :length="pageCount" :total-visible="$vuetify.breakpoint.xsOnly ? 3 : 5" :color="actionColor" :disabled="loading" aria-label="Day End pages" />
      </v-card-actions>
    </v-card>

    <v-dialog ref="filterDialog" v-model="filtersOpen" max-width="600" scrollable v-drag-dialog :fullscreen="$vuetify.breakpoint.xsOnly" content-class="draggable-dialog">
      <v-card tag="form" class="rounded-xl day-end-filter-dialog" :style="{ maxHeight: $vuetify.breakpoint.xsOnly ? '100dvh' : 'calc(100dvh - 48px)' }" @submit.prevent="applyFilters">
        <CardToolbarDialog title="Day End filters" title-id="day-end-filter-title" icon="mdi-filter-variant" @cancel="filtersOpen = false" />
        <div class="day-end-filter-body pa-4 pa-sm-5">
          <div class="day-end-filters">
            <v-select v-model="draftFilters.locationId" :items="locations" item-text="name" item-value="id" label="Location" placeholder="All locations" clearable dense outlined hide-details :color="actionColor" class="day-end-filter-wide" />
            <v-text-field v-model="draftFilters.start" type="date" label="From business date" dense outlined hide-details :max="draftFilters.end" :color="actionColor" />
            <v-text-field v-model="draftFilters.end" type="date" label="To business date" dense outlined hide-details :min="draftFilters.start" :color="actionColor" />
            <v-select v-model="draftFilters.status" :items="statusOptions" label="Day status" placeholder="All statuses" clearable dense outlined hide-details :color="actionColor" />
            <v-select v-model="draftFilters.lightStatus" :items="lightOptions" label="Closing light" placeholder="All closing lights" clearable dense outlined hide-details :color="actionColor" />
          </div>
          <p class="text-caption admin-muted mt-3 mb-0">Dates follow each location’s business date. Closing lights show the recorded performance for that day.</p>
          <v-alert v-if="draftValidationError" type="error" text dense class="mt-4 mb-0" role="alert"><span class="text--primary">{{ draftValidationError }}</span></v-alert>
        </div>
        <v-divider />
        <v-card-actions class="day-end-filter-footer pa-4">
          <v-btn text :color="actionColor" min-height="44" @click="resetFilters">Reset filters</v-btn>
          <v-spacer />
          <v-btn text min-height="44" @click="filtersOpen = false">Cancel</v-btn>
          <v-btn color="primary" depressed min-height="44" type="submit" :disabled="!!draftValidationError">Apply filters</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <DayEndReport v-model="reportOpen" :day-end="selectedDayEnd" :timezone="timezone" />
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import dayEndService from './service';
import DayEndReport from './Report.vue';

export default {
  name: 'BackOfficeDayEnd',
  components: { DayEndReport },
  data() {
    const savedTimezone = localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}timezone`);
    const timezone = this.$moment.tz.zone(savedTimezone || '') ? savedTimezone : this.$moment.tz.guess();
    const defaults = { start: this.$moment().tz(timezone).subtract(14, 'days').format('YYYY-MM-DD'), end: this.$moment().tz(timezone).format('YYYY-MM-DD'), locationId: null, status: null, lightStatus: null };
    return {
      filterDefaults: defaults, filtersOpen: false, draftFilters: { ...defaults },
      timezone, ...defaults, page: 1, pageSize: 20,
      dayEnds: [], availableLocations: null, count: 0, loading: false, errorMessage: '', requestId: 0, selectedDayEnd: null, reportOpen: false,
      statusOptions: [{ text: 'Open', value: 'open' }, { text: 'Closing', value: 'closing' }, { text: 'Closed', value: 'closed' }],
      lightOptions: [{ text: 'Green', value: 'GREEN' }, { text: 'Yellow', value: 'YELLOW' }, { text: 'Red', value: 'RED' }],
      headers: [
        { text: 'Business date', value: 'businessDate', width: 170 }, { text: 'Location', value: 'location', width: 220 }, { text: 'Day status', value: 'status', width: 100 },
        { text: 'Closing light', value: 'lightStatus', width: 120 }, { text: 'Sales', value: 'salesTotal', align: 'end', width: 140 },
        { text: 'Over / short', value: 'overShortTotal', align: 'end', width: 140 }, { text: '', value: 'action', align: 'end', width: 64 },
      ],
    };
  },
  computed: {
    ...mapGetters('global', ['currentUser', 'currentOrganization']),
    actionColor() { return this.$vuetify.theme.dark ? 'primary lighten-4' : 'primary'; },
    locations() {
      if (this.availableLocations) return this.availableLocations;
      const organization = (this.currentUser.Organizations || []).find(item => String(item.id) === String(this.currentOrganization.id));
      return (organization && organization.Locations) || this.currentOrganization.Locations || [];
    },
    validationError() { return this.validateDates(this.start, this.end); },
    draftValidationError() { return this.validateDates(this.draftFilters.start, this.draftFilters.end); },
    hasActiveFilters() { return Object.keys(this.filterDefaults).some(key => (this[key] || null) !== (this.filterDefaults[key] || null)); },
    filterSummary() {
      const location = this.locations.find(item => String(item.id) === String(this.locationId));
      return [location ? location.name : 'All locations', `${this.businessDate(this.start)} – ${this.businessDate(this.end)}`, this.status && this.statusLabel(this.status), this.lightStatus && `${this.lightLabel(this.lightStatus)} light`].filter(Boolean).join(' · ');
    },
    pageCount() { return Math.max(1, Math.ceil(this.count / this.pageSize)); },
  },
  watch: {
    page() { this.loadDayEnds(); },
    filtersOpen(open) {
      if (!open) { this.draftFilters = { start: this.start, end: this.end, locationId: this.locationId, status: this.status, lightStatus: this.lightStatus }; return; }
      this.$nextTick(() => {
        const content = this.$refs.filterDialog && this.$refs.filterDialog.$refs.content;
        if (content) content.setAttribute('aria-labelledby', 'day-end-filter-title');
      });
    },
    'currentOrganization.id'() { this.reportOpen = false; this.selectedDayEnd = null; this.locationId = null; this.availableLocations = null; this.filtersOpen = false; this.reloadFirstPage(); },
  },
  created() { this.loadDayEnds(); },
  beforeDestroy() { this.requestId += 1; },
  methods: {
    validateDates(startValue, endValue) {
      const start = this.$moment(startValue, 'YYYY-MM-DD', true);
      const end = this.$moment(endValue, 'YYYY-MM-DD', true);
      if (!start.isValid() || !end.isValid()) return 'Choose a valid start and end business date.';
      if (end.isBefore(start)) return 'The end date must be on or after the start date.';
      if (end.diff(start, 'days') >= 93) return 'Choose a date range of 93 days or less.';
      return '';
    },
    openFilters(event) {
      if (event && event.currentTarget) event.currentTarget.focus();
      this.draftFilters = { start: this.start, end: this.end, locationId: this.locationId, status: this.status, lightStatus: this.lightStatus };
      this.filtersOpen = true;
    },
    applyFilters() {
      if (this.draftValidationError) return;
      Object.assign(this, this.draftFilters);
      this.filtersOpen = false;
      this.reloadFirstPage();
    },
    reloadFirstPage() { if (this.page !== 1) this.page = 1; else this.loadDayEnds(); },
    resetFilters() { this.draftFilters = { ...this.filterDefaults }; },
    async loadDayEnds() {
      const requestId = ++this.requestId;
      this.dayEnds = []; this.count = 0; this.errorMessage = '';
      if (this.validationError) { this.loading = false; return; }
      this.loading = true;
      try {
        const params = { start: this.start, end: this.end, timezone: this.timezone, limit: this.pageSize, offset: (this.page - 1) * this.pageSize };
        if (this.locationId) params.LocationId = this.locationId;
        if (this.status) params.status = this.status;
        if (this.lightStatus) params.lightStatus = this.lightStatus;
        const response = await dayEndService.getAll(params);
        if (requestId !== this.requestId) return;
        if (!response || !Array.isArray(response.data) || !Number.isFinite(Number(response.count))) throw new Error('Invalid day-end response');
        this.dayEnds = response.data; this.count = Number(response.count);
        if (Array.isArray(response.locations)) this.availableLocations = response.locations;
      } catch (error) {
        if (requestId === this.requestId) this.errorMessage = 'Day ends could not be loaded. Please try again.';
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    openReport(item, event) {
      const target = event && event.currentTarget;
      const button = target && (target.matches('button') ? target : target.querySelector('button'));
      if (button) button.focus();
      this.selectedDayEnd = item; this.reportOpen = true;
    },
    businessDate(value) { return value ? this.$moment(value, 'YYYY-MM-DD').format('MMM D, YYYY') : 'Not recorded'; },
    statusLabel(value) { return ({ open: 'Open', closing: 'Closing', closed: 'Closed' })[value] || 'Not recorded'; },
    lightLabel(value) { return ({ GREEN: 'Green', YELLOW: 'Yellow', RED: 'Red' })[value] || 'Not recorded'; },
    lightColor(value) { return ({ GREEN: 'success', YELLOW: 'warning', RED: 'error' })[value] || undefined; },
    lightIcon(value) { return ({ GREEN: 'mdi-check-circle', YELLOW: 'mdi-alert-circle', RED: 'mdi-close-circle' })[value] || 'mdi-circle-outline'; },
    money(value, item) { return value == null || value === '' || !Number.isFinite(Number(value)) ? 'Not recorded' : this.$options.filters.currency(value, this.currencySymbol(item)); },
    currencySymbol(item) { return item.currencySymbol || (item.Location && item.Location.currencySymbol) || '$'; },
  },
};
</script>

<style scoped>
.day-end-page { min-width: 0; max-width: 100%; }
.day-end-heading { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 4px 16px; }
.day-end-description { grid-column: 1 / -1; }
.day-end-table { max-width: 100%; overflow-x: auto; }
.day-end-data-table { min-width: 954px; }
.day-end-date { white-space: nowrap; }
.day-end-location { display: inline-block; max-width: 220px; overflow-wrap: anywhere; }
.day-end-filter-dialog { display: flex; flex-direction: column; overflow: hidden; }
.day-end-filter-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; }
.day-end-filter-footer { flex: 0 0 auto; flex-wrap: wrap; gap: 8px; }
.day-end-filters { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; align-items: center; }
.day-end-filter-wide { grid-column: 1 / -1; }
.day-end-light { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
.day-end-money { white-space: nowrap; font-variant-numeric: tabular-nums; }
.day-end-pagination { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
@media (max-width: 599px) { .day-end-filters { grid-template-columns: 1fr; gap: 12px; } .day-end-pagination { justify-content: center; } }
</style>
